import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRightIcon, FileTextIcon, GiftIcon, GlobeIcon, HeadphonesIcon, HeartIcon, HeartPulseIcon, KeyRoundIcon, LockKeyholeIcon, LogOutIcon, MapPinIcon, PackageIcon, ReceiptTextIcon, RotateCcwIcon, TicketIcon, WalletIcon } from 'lucide-react';
import { useStore } from '../../lib/store';
type MenuItem = {
  icon: typeof PackageIcon;
  label: string;
  detail?: string;
  path?: string;
  requiresAuth?: boolean;
  action?: 'language' | 'password' | 'signout';
};
interface AccountMenuListProps {
  isAuthenticated: boolean;
  onSignIn: () => void;
  onRegister: () => void;
  onLanguageClick?: () => void;
  onPasswordClick?: () => void;
}
const menuGroups: Array<{
  title: string;
  items: MenuItem[];
}> = [{
  title: 'Orders & payments',
  items: [{
    icon: PackageIcon,
    label: 'My orders',
    detail: 'Track and reorder',
    path: '/account/orders',
    requiresAuth: true
  }, {
    icon: ReceiptTextIcon,
    label: 'Payment requests',
    path: '/account/payment-requests',
    requiresAuth: true
  }, {
    icon: WalletIcon,
    label: 'Wallet',
    detail: 'Balance and activity',
    path: '/account/wallet',
    requiresAuth: true
  }, {
    icon: GiftIcon,
    label: 'Rabeh Rewards',
    detail: 'Points and benefits',
    path: '/account/points',
    requiresAuth: true
  }, {
    icon: HeartIcon,
    label: 'Wishlist',
    path: '/account/wishlist',
    requiresAuth: true
  }, {
    icon: TicketIcon,
    label: 'Coupons',
    path: '/account/coupons',
    requiresAuth: true
  }, {
    icon: RotateCcwIcon,
    label: 'Returns',
    path: '/account/returns',
    requiresAuth: true
  }]
}, {
  title: 'Health & RX',
  items: [{
    icon: HeartPulseIcon,
    label: 'RX Health',
    detail: 'Profile, family and reminders',
    path: '/rx',
    requiresAuth: true
  }, {
    icon: FileTextIcon,
    label: 'My prescriptions',
    path: '/prescription/history',
    requiresAuth: true
  }]
}, {
  title: 'Settings & support',
  items: [{
    icon: MapPinIcon,
    label: 'Addresses',
    path: '/account/addresses',
    requiresAuth: true
  }, {
    icon: KeyRoundIcon,
    label: 'Login & security',
    action: 'password',
    requiresAuth: true
  }, {
    icon: GlobeIcon,
    label: 'Language',
    action: 'language'
  }, {
    icon: HeadphonesIcon,
    label: 'Help & support',
    path: '/help-center'
  }]
}];
export function AccountMenuList({
  isAuthenticated,
  onSignIn,
  onRegister: _onRegister,
  onLanguageClick,
  onPasswordClick
}: AccountMenuListProps) {
  const navigate = useNavigate();
  const language = useStore(state => state.language);
  const setAuthenticated = useStore(state => state.setAuthenticated);
  const handleItemClick = (item: MenuItem) => {
    if (item.requiresAuth && !isAuthenticated) {
      onSignIn();
      return;
    }
    if (item.action === 'language') onLanguageClick?.();else if (item.action === 'password') onPasswordClick?.();else if (item.action === 'signout') {
      setAuthenticated(false);
      navigate('/');
    } else if (item.path) navigate(item.path);
  };
  return <div className="space-y-5" data-id="element-112" data-mp="23kxinsca34nx">
    {menuGroups.map(group => <section key={group.title} aria-labelledby={group.title.replaceAll(' ', '-').toLowerCase()} data-id="element-113" data-mp="23micffqrqjkq">
      <h2 id={group.title.replaceAll(' ', '-').toLowerCase()} className="mb-2 px-1 text-xs font-bold uppercase tracking-[0.1em] text-gray-500" data-id="element-114" data-mp="28l1x7ctmlpbi">{group.title}</h2>
      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-[0_2px_10px_rgba(15,23,42,0.04)]" data-id="element-115" data-mp="2ii6aen5huag6">
        {group.items.map(item => {
          const Icon = item.icon;
          const detail = item.action === 'language' ? language === 'ar' ? 'Arabic' : 'English' : item.detail;
          return <button key={item.label} type="button" onClick={() => handleItemClick(item)} className="flex min-h-14 w-full items-center gap-3 border-b border-gray-100 px-4 py-3 text-left last:border-b-0 transition-[background-color] duration-150 hover:bg-gray-50" data-id="element-116" data-mp="2w9l69uki264n">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#f1f8f7] text-[#16726c]" data-id="element-117" data-mp="3a7t0vvu2cvcn"><Icon className="h-[18px] w-[18px]" data-id="element-118" data-mp="br1e9li357ft" /></span>
            <span className="min-w-0 flex-1" data-id="element-119" data-mp="6szv70knt84c"><span className="block text-sm font-semibold text-gray-900" data-id="element-120" data-mp="6trscftwndwq">{item.label}</span>{detail && <span className="mt-0.5 block truncate text-xs text-gray-500" data-id="element-121" data-mp="kkwk6vr171wi">{detail}</span>}</span>
            {!isAuthenticated && item.requiresAuth ? <LockKeyholeIcon className="h-4 w-4 shrink-0 text-gray-400" aria-label="Sign in required" data-id="element-122" data-mp="gjbcdotctdt6" /> : <ChevronRightIcon className="h-5 w-5 shrink-0 text-gray-400" data-id="element-123" data-mp="3oze8qyf0ng27" />}
          </button>;
        })}
      </div>
    </section>)}
    {isAuthenticated && <button type="button" onClick={() => handleItemClick({
      icon: LogOutIcon,
      label: 'Sign out',
      action: 'signout'
    })} className="flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-rose-200 bg-rose-50 text-sm font-bold text-rose-700 transition-[background-color] duration-150 hover:bg-rose-100" data-id="element-124" data-mp="1tvk24e6d0bd"><LogOutIcon className="h-[18px] w-[18px]" data-id="element-125" data-mp="18ah3jfr3k16b" />Sign out</button>}
  </div>;
}